import * as React from 'react';
import { Card, CardBody, Grid, GridItem, Title } from '@patternfly/react-core';
import { aggregate } from '../../../../shared/riskregister/aggregate';
import { completedBase } from '../../../../shared/riskregister/filters';
import type { Payload, Row, ViewState } from '../../../../shared/riskregister/types';

interface PositionTile {
  title: string;
  value: number | string;
  subtitle: string;
  tone: 'danger' | 'warning' | 'info' | 'success' | 'neutral';
}

const PositionTile: React.FunctionComponent<PositionTile> = ({ title, value, subtitle, tone }) => (
  <Card isCompact className={`risk-register__metric-card risk-register__metric-card--${tone}`}>
    <CardBody>
      <Title headingLevel="h3" size="md">
        {title}
      </Title>
      <strong aria-label={`${title}: ${value}`}>{value}</strong>
      <p>{subtitle}</p>
    </CardBody>
  </Card>
);

export const PositionTiles: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
}> = ({ payload, rows, state }) => {
  const current = React.useMemo(() => aggregate(rows, payload), [payload, rows]);
  const completed = React.useMemo(
    () => aggregate(completedBase(payload.rows, state, payload), payload),
    [payload, state],
  );
  const oldestOverdue = React.useMemo(
    () =>
      rows
        .filter((row) => row.open && row.due_status === 'overdue')
        .reduce((oldest, row) => Math.max(oldest, Math.abs(row.due_days || 0)), 0),
    [rows],
  );
  const completedPercentage = completed.n ? Math.round((completed.closed / completed.n) * 100) : 0;

  const tiles: PositionTile[] = [
    {
      title: 'Overdue',
      value: current.overdue,
      tone: 'danger',
      subtitle: current.overdue ? `Oldest item is ${oldestOverdue} days past due.` : 'No open items are overdue.',
    },
    {
      title: `Due within ${payload.at_risk_days} days`,
      value: current.atrisk,
      tone: 'warning',
      subtitle: 'Open items approaching their committed due date.',
    },
    {
      title: `Idle ${payload.stale_days} days or more`,
      value: current.stale,
      tone: 'warning',
      subtitle: `${current.stalled} have had no status movement for ${payload.stalled_days} days or more.`,
    },
    {
      title: 'Open items',
      value: current.open,
      tone: 'info',
      subtitle: `${current.todo} not started and ${current.prog} in progress.`,
    },
    {
      title: 'Completed',
      value: completed.closed,
      tone: 'success',
      subtitle: `${completedPercentage}% of ${completed.n} tracked items; this ignores scope, due, and stage filters.`,
    },
    {
      title: 'Weighted risk',
      value: current.weighted,
      tone: 'info',
      subtitle: `Across ${current.measured} assessed open item${current.measured === 1 ? '' : 's'}.`,
    },
    {
      title: 'Due date slipped',
      value: current.slipped,
      tone: 'warning',
      subtitle: `${current.reslipped} slipped more than once; ${current.moved} moved at all.`,
    },
    {
      title: 'Unassigned',
      value: current.unassigned,
      tone: 'danger',
      subtitle: 'Open items without a named owner.',
    },
    {
      title: 'No risk score',
      value: current.unscored,
      tone: 'warning',
      subtitle: 'Open items without an assessed risk score.',
    },
    {
      title: 'No risk category',
      value: current.nocat,
      tone: 'neutral',
      subtitle: 'Open items without a risk category in Jira.',
    },
  ];

  return (
    <Grid hasGutter className="risk-register__metric-grid">
      {tiles.map((tile) => (
        <GridItem key={tile.title} span={12} sm={6} lg={3}>
          <PositionTile {...tile} />
        </GridItem>
      ))}
    </Grid>
  );
};
